import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { MSection, SectionHeader } from '@/components/marketing/section'

export const Route = createFileRoute('/_site/integrations/email')({
  component: EmailIntegrationPage,
  head: () => ({
    meta: [
      { title: 'Email Uptime Alerts & Downtime Notifications — Uptrack' },
      { name: 'description', content: 'Get email downtime notifications the moment your site goes down. Default alert channel on all plans. Digest options and custom sender domain on Business.' },
      { property: 'og:title', content: 'Email Uptime Alerts & Downtime Notifications — Uptrack' },
      { property: 'og:description', content: 'Email downtime alerts on all plans. Digest options and custom sender domain on Business.' },
      { property: 'og:image', content: 'https://uptrack.app/og/integrations-email.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/integrations/email' },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'Integrations', item: 'https://uptrack.app/integrations' },
            { '@type': 'ListItem', position: 3, name: 'Email', item: 'https://uptrack.app/integrations/email' },
          ],
        }),
      },
    ],
  }),
})

function EmailIntegrationPage() {
  return (
    <>
      {/* Hero */}
      <section>
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="max-w-3xl">
            <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">
              Email uptime alerts & downtime notifications.
            </h1>
            <p className="mt-5 max-w-2xl text-lead text-body">
              Email is the universal alert channel. Every Uptrack account comes with email alerts enabled by default — no setup required, no extra cost.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">Available on all plans</p>
            <div className="mt-8">
              <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
                <Link to="/pricing">
                  See plans
                  <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">from $3/mo · 30-day money-back guarantee</p>
          </div>
        </div>
      </section>

      {/* Why */}
      <MSection tone="raised">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeader
              title="The default alert channel"
              lead="Not everyone uses Slack or Discord. Email reaches every team member regardless of their tools. It is the baseline that ensures no alert goes undelivered."
            />
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-lg border border-border bg-background p-6 text-sm">
              <p className="text-muted-foreground">From: <span className="text-foreground">alerts@uptrack.app</span></p>
              <p className="text-muted-foreground">Subject: <span className="text-status-down font-semibold">[DOWN] api.example.com is not responding</span></p>
              <hr className="my-3 border-border" />
              <p className="text-muted-foreground">Your monitor <span className="text-foreground font-medium">Production API</span> has failed 3 consecutive checks.</p>
              <p className="text-muted-foreground mt-1">Status: 503 | Response time: 12,340ms | Started: 08:23 UTC</p>
            </div>
          </div>
        </div>
      </MSection>

      {/* How to set up */}
      <MSection>
        <SectionHeader title="How to set up email alerts" />
        <ol className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {[
            { step: '1', title: 'Already enabled by default', desc: 'When you create an Uptrack account, email alerts are automatically configured for your account email. Zero setup.' },
                { step: '2', title: 'Add team members', desc: 'Invite teammates to your organization. Each member can enable email alerts for the monitors they care about.' },
                { step: '3', title: 'Configure digest and sender (Business)', desc: 'On Business plans, group alerts into digest emails and use a custom sender domain for better deliverability.' },
          ].map((item) => (
            <li key={item.step} className="rounded-lg border border-border bg-card p-6">
              <span className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background num text-sm font-semibold text-foreground">
                {item.step}
              </span>
              <h3 className="mt-4 text-[17px] font-semibold text-foreground">{item.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-body">{item.desc}</p>
            </li>
          ))}
        </ol>
      </MSection>

      {/* What you get */}
      <MSection tone="raised">
        <SectionHeader title="What you get" />
        <ul className="mt-8 grid grid-cols-1 gap-x-10 gap-y-3 md:grid-cols-2">
          {[
            'Enabled by default on all plans — zero configuration',
                  'Downtime and recovery notifications',
                  'Full incident context: status code, response time, failure count',
                  'Per-monitor alert assignment',
                  'Multiple recipient emails per organization',
                  'Digest mode to reduce alert fatigue (Business)',
                  'Custom sender domain for deliverability (Business)',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 border-t border-border pt-3 text-[15px] text-body">
              <span aria-hidden="true" className="mt-0.5 font-mono text-status-up">✓</span>
              {item}
            </li>
          ))}
        </ul>
      </MSection>

      {/* CTA */}
      <section>
        <div className="container mx-auto px-4 py-12 md:py-[72px]">
          <div className="grid grid-cols-1 items-center gap-8 rounded-frame border border-border bg-card p-8 shadow-raised md:p-12 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight md:text-headline">Email alerts, ready from day one</h2>
              <p className="mt-3 max-w-xl text-lead text-body">
                Every plan includes 30-second checks and alert confirmation.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
                <Link to="/pricing">
                  See plans
                  <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <p className="mt-3 text-sm text-muted-foreground">from $3/mo · 30-day money-back guarantee</p>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
